import React, { useEffect, useState } from "react";
import PrivateLayout from "layouts/private/private";
import { LicensesApi } from "@api/licensesApi";
import { IShortLicenseInfo } from "@models/dto/Rocketta/LicenseServer/Models/Licenses/IShortLicenseInfo";
import {
  FocusZone,
  FocusZoneDirection,
  IList,
  ITheme,
  List,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  normalize,
  TextField,
  useTheme,
} from "@fluentui/react";
import { extractErrorMessage } from "@helpers/alertUtils";

const numberOfItemsOnPage = 50;
const itemHeight = 25;
const generateStyles = (theme: ITheme) => {
  return mergeStyleSets({
    container: {
      overflow: "auto",
      maxHeight: 400,
      border: "1px solid " + theme.palette.neutralLight,
      marginTop: 20,
      selectors: {
        ".ms-List-cell .odd": {
          height: itemHeight,
          lineHeight: itemHeight,
        },
        ".ms-List-cell .even": {
          height: itemHeight,
          lineHeight: itemHeight,
          background: theme.palette.neutralLighter,
        },
      },
    },
    itemWrapper: {
      display: "flex",
      alignItems: "center",
      marginBottom: "5px",
    },
    itemContent: [
      theme.fonts.medium,
      normalize,
      {
        position: "relative",
        boxSizing: "border-box",
        display: "block",
        borderLeft: "3px solid " + theme.palette.themePrimary,
        paddingLeft: 27,
      },
    ],
  });
};

export const Home: React.FC = () => {
  const [licenses, setLicenses] = useState<IShortLicenseInfo[]>([]);
  const [items, setItems] = useState<IShortLicenseInfo[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const listRef: React.RefObject<IList> = React.useRef(null);
  const theme = useTheme();
  const classNames = React.useMemo(() => generateStyles(theme), [theme]);

  useEffect(() => {
    LicensesApi.getLicenses()
      .then((result) => {
        const licensesItems = result.data.data;
        setLicenses(licensesItems);
        setItems(licensesItems);
        return licensesItems;
      })
      .catch((err) => {
        const message = extractErrorMessage(err);
        setErrorMessage(message);
      });
  }, []);

  const getPageHeight = (idx: number): number => {
    let h = 0;
    for (let i = idx; i < idx + numberOfItemsOnPage; ++i) {
      h += itemHeight;
    }
    return h;
  };

  const onRenderCell = React.useCallback(
    (item: IShortLicenseInfo, index: number): JSX.Element => {
      return (
        <div data-is-focusable className={classNames.itemWrapper}>
          <div className={classNames.itemContent}>
            {item.id} &nbsp; {item.url}, expiration date: {item.expirationDate}, product: {item.product.name} (
            {item.product.productId})
          </div>
        </div>
      );
    },
    [classNames]
  );

  const onFilterChanged = (_: any, text: string): void => {
    setItems(
      licenses.filter(
        (item) =>
          item.url.toLowerCase().indexOf(text.toLowerCase()) >= 0 ||
          item.product.name.toLowerCase().indexOf(text.toLowerCase()) >= 0 ||
          item.product.productId.toLowerCase().indexOf(text.toLowerCase()) >= 0
      )
    );
  };

  return (
    <PrivateLayout>
      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true} dismissButtonAriaLabel="Close">
          {errorMessage}
        </MessageBar>
      )}
      <h1>Licenses</h1>
      <FocusZone direction={FocusZoneDirection.vertical}>
        <TextField
          label={"Filter"}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={onFilterChanged}
        />
        <List componentRef={listRef} items={items} getPageHeight={getPageHeight} onRenderCell={onRenderCell} />
      </FocusZone>
    </PrivateLayout>
  );
};
